"use client";

import { useBoundStore } from "@frontend/lib/stores/globalStore";
import type { SelectedAppointmentTimeEnum } from "@frontend/lib/stores/slices/bookingSlice";
import { addDays } from "date-fns";
import { startOfDay } from "date-fns";
import { createContext, useContext, useState } from "react";
import { DEFAULT_BOOKING_LEAD_TIME_DAYS, getNext5Days } from "../utils"; // Assuming this exists or needs to be moved

interface BookingContextType {
  currentDisplayDates: Date[];
  setCurrentDisplayDates: (dates: Date[]) => void;
  startOfDayAfterTomorrow: Date;
  selectedDay: Date;
  setSelectedDay: (day: Date) => void;
  leadTimeDays: number;
  setLeadTimeDays: (days: number) => void;
  isVirtualVisit: boolean;
  setIsVirtualVisit: (isVirtualVisit: boolean) => void;
  selectedPracticeId: number | null;
  setSelectedPracticeId: (practiceId: number | null) => void;
  selectedAppointmentTime: Date | SelectedAppointmentTimeEnum | null;
  setSelectedAppointmentTime: (
    appointmentTime: Date | SelectedAppointmentTimeEnum | null,
  ) => void;
}

const BookingContext = createContext<BookingContextType | undefined>(undefined);

export function BookingProvider({
  children,
  orgId,
}: {
  children: React.ReactNode;
  orgId: number;
}) {
  // Note: Initial dates use browser timezone as fallback.
  // Components with access to practice timezone (like AppointmentDateTimeSelector)
  // should use getStartOfTodayInTimezone() and update these dates when timezone is available.
  const startOfToday = startOfDay(new Date());
  const experityBooking = orgId === 24;
  // Booking lead time (days until the earliest bookable day). Defaults to 2 but
  // an HRT can override it (see getBookingLeadTimeDays); Booking sets it once the
  // cart's HRTs are known.
  const [leadTimeDays, setLeadTimeDays] = useState<number>(
    DEFAULT_BOOKING_LEAD_TIME_DAYS,
  );
  const startOfDayAfterTomorrow = addDays(startOfToday, leadTimeDays);
  const [currentDisplayDates, setCurrentDisplayDates] = useState<Date[]>(() =>
    getNext5Days(
      experityBooking
        ? startOfToday
        : addDays(startOfToday, DEFAULT_BOOKING_LEAD_TIME_DAYS),
    ),
  );
  const [selectedDay, setSelectedDay] = useState<Date>(
    addDays(startOfToday, DEFAULT_BOOKING_LEAD_TIME_DAYS),
  );
  // Note: the date carousel is re-anchored to the overridden earliest bookable
  // day by Booking (which owns getBookingLeadTimeDays) in a single effect, so
  // there is no re-anchoring effect here.
  const [isVirtualVisit, setIsVirtualVisit] = useState<boolean>(false);
  // Use global store for selectedPracticeId to persist across pages
  const selectedPracticeId = useBoundStore((state) => state.practiceId);
  const setSelectedPracticeId = useBoundStore((state) => state.setPracticeId);
  // Use global store for selectedAppointmentTime to persist across pages
  const selectedAppointmentTime = useBoundStore(
    (state) => state.selectedAppointmentTime,
  );
  const setSelectedAppointmentTime = useBoundStore(
    (state) => state.setSelectedAppointmentTime,
  );
  return (
    <BookingContext.Provider
      value={{
        currentDisplayDates,
        setCurrentDisplayDates,
        startOfDayAfterTomorrow,
        selectedDay,
        setSelectedDay,
        leadTimeDays,
        setLeadTimeDays,
        isVirtualVisit,
        setIsVirtualVisit,
        selectedPracticeId,
        setSelectedPracticeId,
        selectedAppointmentTime,
        setSelectedAppointmentTime,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
}

export function useBooking() {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error("useBooking must be used within a BookingProvider");
  }
  return context;
}
